import { PLATFORM_CURRENCY, PLATFORM_LOCALE } from '../../lib/currency';
import { getWorkspaceId } from '../../lib/auth-storage';
import { useCallback, useEffect, useState } from 'react';
import {
  billingApi,
  type ActivityEntry,
  type CommercialDashboard,
  type CommercialNotification,
  type CreditAccount,
  type CreditTransaction,
  type Entitlement,
  type InvoiceRecord,
  type PaymentRecord,
  type PricingPlan,
  type RevenueAnalytics,
  type Subscription,
  type UsageDashboard,
} from '../../api/billing';

const WORKSPACE = (getWorkspaceId() ?? '');

type Tab = 'overview' | 'pricing' | 'subscription' | 'invoices' | 'credits' | 'usage' | 'analytics' | 'notifications';

export function BillingDashboardPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<CommercialDashboard | null>(null);
  const [pricing, setPricing] = useState<PricingPlan[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [invoices, setInvoices] = useState<InvoiceRecord[]>([]);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [credits, setCredits] = useState<CreditAccount | null>(null);
  const [creditTxs, setCreditTxs] = useState<CreditTransaction[]>([]);
  const [usage, setUsage] = useState<UsageDashboard | null>(null);
  const [analytics, setAnalytics] = useState<RevenueAnalytics | null>(null);
  const [entitlements, setEntitlements] = useState<Entitlement[]>([]);
  const [notifications, setNotifications] = useState<CommercialNotification[]>([]);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, plans, subs, invs, pays, bal, txs, usg, rev, ents, notifs, act] = await Promise.all([
        billingApi.dashboard(WORKSPACE),
        billingApi.pricing(),
        billingApi.listSubscriptions(),
        billingApi.listInvoices(),
        billingApi.listPayments(),
        billingApi.creditBalance(WORKSPACE),
        billingApi.listCreditTransactions(),
        billingApi.usageDashboard(WORKSPACE),
        billingApi.revenueAnalytics(),
        billingApi.listEntitlements(),
        billingApi.listNotifications(true),
        billingApi.listActivity(),
      ]);
      setDashboard(dash);
      setPricing(plans);
      setSubscriptions(subs);
      setInvoices(invs);
      setPayments(pays);
      setCredits(bal);
      setCreditTxs(txs);
      setUsage(usg);
      setAnalytics(rev);
      setEntitlements(ents);
      setNotifications(notifs);
      setActivity(act);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load billing dashboard');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const startTrial = async () => {
    setBusy(true);
    try {
      await billingApi.subscribe({ plan_id: 'plan-trial', workspace_id: WORKSPACE });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const checkoutMonthly = async () => {
    setBusy(true);
    try {
      await billingApi.checkout({ plan_id: 'plan-monthly', workspace_id: WORKSPACE, provider: 'stripe' });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  // Amounts arrive in minor units. Default to INR/en-IN; an explicit currency on
  // the record still wins so a foreign-currency invoice renders correctly.
  const formatMoney = (cents: number, currency = PLATFORM_CURRENCY) =>
    new Intl.NumberFormat(PLATFORM_LOCALE, { style: 'currency', currency }).format(cents / 100);

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'subscription', label: 'Subscription' },
    { id: 'invoices', label: 'Invoices' },
    { id: 'credits', label: 'Credits' },
    { id: 'usage', label: 'Usage' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'notifications', label: 'Notifications' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Billing &amp; Subscriptions</h1>
          <p className="text-sm text-slate-500">
            Plans, credits, usage metering, invoicing &amp; entitlements — FCP-00011
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={startTrial}
            className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
          >
            Start Trial
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={checkoutMonthly}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            Checkout Professional
          </button>
          <button type="button" onClick={refresh} className="rounded-lg border border-slate-200 px-4 py-2 text-sm hover:bg-slate-50">
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm ${
              tab === t.id ? 'bg-violet-100 font-medium text-violet-800' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Credit Balance" value={dashboard.credit_available.toLocaleString()} />
            <StatCard label="Trial Days Left" value={dashboard.trial_days_remaining} />
            <StatCard label="Pending Invoices" value={dashboard.pending_invoices} />
            <StatCard label="Entitlements" value={dashboard.entitlements_active} />
            <StatCard label="Unpaid" value={formatMoney(dashboard.unpaid_amount_cents)} />
            <StatCard label="Unread Alerts" value={dashboard.unread_notifications} />
          </div>
          {dashboard.current_plan && (
            <div className="rounded-xl border border-violet-200 bg-violet-50 p-4">
              <p className="font-medium text-violet-900">{dashboard.current_plan.name}</p>
              <p className="text-sm text-violet-700">
                {dashboard.current_plan.billing_cycle} · {formatMoney(dashboard.current_plan.price_cents)}/
                {dashboard.current_plan.billing_cycle === 'annual' ? 'yr' : 'mo'}
              </p>
            </div>
          )}
          <Panel title="Recent Activity">
            <ActivityList items={activity.length ? activity : dashboard.recent_activity ?? []} />
          </Panel>
        </div>
      )}

      {tab === 'pricing' && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {pricing.map((p) => (
            <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between">
                <p className="font-semibold text-slate-900">{p.name}</p>
                {p.featured && <Badge text="popular" tone="violet" />}
              </div>
              <p className="mt-2 text-2xl font-bold">{formatMoney(p.price_cents)}</p>
              <p className="text-xs uppercase text-slate-400">{p.billing_cycle} · {p.support_tier}</p>
              {p.credit_grant ? (
                <p className="mt-2 text-sm text-violet-700">{p.credit_grant.toLocaleString()} credits included</p>
              ) : null}
              {p.features && (
                <ul className="mt-3 space-y-1 text-sm text-slate-600">
                  {p.features.slice(0, 4).map((f) => (
                    <li key={f.key}>✓ {f.name ?? f.key}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'subscription' && (
        <div className="space-y-4">
          {subscriptions.length === 0 ? (
            <p className="text-sm text-slate-500">No active subscription. Start a trial or checkout a plan.</p>
          ) : (
            subscriptions.map((s) => (
              <div key={s.id} className="rounded-xl border border-slate-200 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-medium">Plan: {s.plan_id}</p>
                    <p className="text-sm text-slate-500">
                      Period: {s.current_period_start} → {s.current_period_end}
                    </p>
                  </div>
                  <Badge text={s.status} tone={s.status === 'active' ? 'green' : 'amber'} />
                </div>
              </div>
            ))
          )}
          <Panel title="Feature Entitlements">
            {entitlements.length === 0 ? (
              <p className="text-sm text-slate-500">Subscribe to unlock entitlements.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {entitlements.map((e) => (
                  <Badge key={e.feature_key} text={e.feature_key} tone={e.enabled ? 'green' : 'red'} />
                ))}
              </div>
            )}
          </Panel>
        </div>
      )}

      {tab === 'invoices' && (
        <div className="grid gap-6 lg:grid-cols-2">
          <Panel title="Invoices">
            {invoices.length === 0 ? (
              <p className="text-sm text-slate-500">No invoices yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-500">
                    <th className="py-2">Number</th>
                    <th>Total</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="border-b border-slate-100">
                      <td className="py-2">{inv.invoice_number}</td>
                      <td>{formatMoney(inv.total_cents, inv.currency)}</td>
                      <td><Badge text={inv.status} tone={inv.status === 'completed' ? 'green' : 'amber'} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
          <Panel title="Payment History">
            {payments.length === 0 ? (
              <p className="text-sm text-slate-500">No payments yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100 text-sm">
                {payments.map((p) => (
                  <li key={p.id} className="flex justify-between py-2">
                    <span>{p.provider}</span>
                    <span>{formatMoney(p.amount_cents)} · <Badge text={p.status} tone={p.status === 'succeeded' ? 'green' : 'red'} /></span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      )}

      {tab === 'credits' && (
        <div className="space-y-4">
          {credits && (
            <div className="grid gap-4 sm:grid-cols-3">
              <StatCard label="Balance" value={credits.balance.toLocaleString()} />
              <StatCard label="Reserved" value={credits.reserved.toLocaleString()} />
              <StatCard label="Available" value={credits.available.toLocaleString()} />
            </div>
          )}
          <Panel title="Credit Transactions">
            {creditTxs.length === 0 ? (
              <p className="text-sm text-slate-500">No transactions yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100 text-sm">
                {creditTxs.map((tx) => (
                  <li key={tx.id} className="flex justify-between py-2">
                    <span>{tx.type} — {tx.description ?? ''}</span>
                    <span className={tx.amount >= 0 ? 'text-emerald-700' : 'text-red-700'}>
                      {tx.amount >= 0 ? '+' : ''}{tx.amount} → {tx.balance_after}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      )}

      {tab === 'usage' && usage && (
        <Panel title="Usage This Period">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(usage.by_type).map(([k, v]) => (
              <StatCard key={k} label={k} value={v.toLocaleString()} />
            ))}
            {Object.keys(usage.by_type).length === 0 && (
              <p className="text-sm text-slate-500">No usage recorded this period.</p>
            )}
          </div>
        </Panel>
      )}

      {tab === 'analytics' && analytics && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="MRR" value={formatMoney(analytics.mrr)} />
          <StatCard label="ARR" value={formatMoney(analytics.arr)} />
          <StatCard label="Churn" value={`${analytics.churn_rate.toFixed(1)}%`} />
          <StatCard label="ARPU" value={formatMoney(analytics.arpu)} />
          <StatCard label="LTV" value={formatMoney(analytics.ltv)} />
          <StatCard label="Credit Consumption" value={analytics.credit_consumption.toLocaleString()} />
        </div>
      )}

      {tab === 'notifications' && (
        <Panel title="Billing Notifications">
          {notifications.length === 0 ? (
            <p className="text-sm text-slate-500">No unread notifications.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {notifications.map((n) => (
                <li key={n.id} className="py-3 text-sm">
                  <p className="font-medium">{n.title}</p>
                  {n.body && <p className="text-slate-600">{n.body}</p>}
                </li>
              ))}
            </ul>
          )}
        </Panel>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-slate-900">{value}</p>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="mb-4 text-lg font-medium text-slate-900">{title}</h2>
      {children}
    </section>
  );
}

function Badge({ text, tone }: { text: string; tone: 'green' | 'amber' | 'red' | 'violet' }) {
  const colors = {
    green: 'bg-emerald-100 text-emerald-800',
    amber: 'bg-amber-100 text-amber-800',
    red: 'bg-red-100 text-red-800',
    violet: 'bg-violet-100 text-violet-800',
  };
  return <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${colors[tone]}`}>{text}</span>;
}

function ActivityList({ items }: { items: ActivityEntry[] }) {
  if (items.length === 0) return <p className="text-sm text-slate-500">No recent activity.</p>;
  return (
    <ul className="divide-y divide-slate-100">
      {items.map((a) => (
        <li key={a.id} className="py-2 text-sm">
          <span className="font-medium">{a.action}</span> — {a.message}
        </li>
      ))}
    </ul>
  );
}
